import { useCallback, useEffect, useRef, useState } from 'react';
import { callUIAPI } from '@utils/apiUtils';
import { Check, X } from 'lucide-react';
import { _t } from '@/i18n/t';

const fieldClass =
  'w-full rounded-lg border border-solid border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring';

interface ContactModalProps {
  open: boolean;
  onClose: () => void;
  initialName?: string;
  initialCompany?: string;
  source?: string;
}

const ContactModal = ({ open, onClose, initialName = '', initialCompany = '', source = 'site_health' }: ContactModalProps) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [vals, setVals] = useState({ name: initialName, company: initialCompany, phone: '' });
  const [touched, setTouched] = useState({ name: false, company: false });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const errs = {
    name: !vals.name.trim() ? _t('scanReport.contact.errorNameRequired') : '',
    company: !vals.company.trim() ? _t('scanReport.contact.errorCompanyRequired') : '',
  };
  const canSubmit = !errs.name && !errs.company;

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setTouched({ name: true, company: true });
      if (!canSubmit) return;
      setSubmitting(true);
      try {
        await callUIAPI(
          'firms/onboarding-contact',
          { name: vals.name.trim(), company: vals.company.trim(), phone: vals.phone.trim() || undefined, source },
          'post',
          true,
        );
      } catch {}
      setSubmitting(false);
      setSent(true);
    },
    [canSubmit, vals, source],
  );

  useEffect(() => {
    if (open) {
      setVals({ name: initialName, company: initialCompany, phone: '' });
      setTouched({ name: false, company: false });
      setSent(false);
      setSubmitting(false);
    }
  }, [open, initialName, initialCompany]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
    >
      <div className="relative w-full max-w-md rounded-2xl border border-solid border-border bg-background shadow-xl p-6">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground bg-transparent cursor-pointer"
          aria-label={_t('scanReport.contact.close')}
        >
          <X className="h-4 w-4" strokeWidth={1.75} />
        </button>

        {sent ? (
          <div className="flex flex-col items-center text-center gap-4 py-4">
            <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600">
              <Check className="h-5 w-5" strokeWidth={2} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">{_t('scanReport.contact.successTitle')}</h2>
              <p className="text-sm text-muted-foreground mt-1">
                {_t('scanReport.contact.successMessage', { company: vals.company || 'you' })}
              </p>
            </div>
            <div className="flex items-center gap-2 border border-border rounded-lg px-3 py-2.5 text-xs text-muted-foreground bg-muted/40 mt-2">
              {_t('scanReport.contact.responseTime')}
            </div>
          </div>
        ) : (
          <>
            <h2 className="text-lg font-semibold text-foreground mb-1">{_t('scanReport.contact.title')}</h2>
            <p className="text-sm text-muted-foreground mb-5">
              {_t('scanReport.contact.tagline')}
            </p>
            <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-foreground">{_t('scanReport.contact.fieldName')}</label>
                <input
                  type="text"
                  className={fieldClass}
                  value={vals.name}
                  onChange={(e) => setVals((v) => ({ ...v, name: e.target.value }))}
                  onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                  placeholder={_t('scanReport.contact.namePlaceholder')}
                  autoFocus
                />
                {touched.name && errs.name && (
                  <span className="text-xs text-destructive">{errs.name}</span>
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-foreground">{_t('scanReport.contact.fieldCompany')}</label>
                <input
                  type="text"
                  className={fieldClass}
                  value={vals.company}
                  onChange={(e) => setVals((v) => ({ ...v, company: e.target.value }))}
                  onBlur={() => setTouched((t) => ({ ...t, company: true }))}
                  placeholder={_t('scanReport.contact.companyPlaceholder')}
                />
                {touched.company && errs.company && (
                  <span className="text-xs text-destructive">{errs.company}</span>
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-foreground">
                  {_t('scanReport.contact.fieldPhone')} <span className="text-muted-foreground font-normal">{_t('scanReport.contact.optional')}</span>
                </label>
                <input
                  type="tel"
                  className={fieldClass}
                  value={vals.phone}
                  onChange={(e) => setVals((v) => ({ ...v, phone: e.target.value }))}
                  placeholder={_t('scanReport.contact.phonePlaceholder')}
                  autoComplete="tel"
                />
              </div>
              <button
                type="submit"
                data-th-button={true}
                disabled={submitting}
                className="mt-1 w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60 cursor-pointer transition-colors"
              >
                {submitting ? _t('scanReport.contact.sending') : _t('scanReport.contact.submit')}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ContactModal;
